import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment';

import { ReactComponent as CrossIcon } from '../../../../assets/images/cross-secondary.svg';
import { ReactComponent as GoogleIcon } from '../../../../assets/images/Google.svg';
import { ReactComponent as MicrosoftIcon } from '../../../../assets/images/microsoft.svg';
import { ReactComponent as AddIcon } from '../../../../assets/images/plus-normal.svg';
import BackButton from '../../../../components/shared/BackButton/BackButton';
import Button from '../../../../components/shared/Button/Button';
import Menu from '../../../../components/shared/Menu/Menu';
import Switch from '../../../../components/shared/Switch/Switch';
import {
  deleteSync,
  fetchImportHistoryCount,
  getSyncData,
  toggleConnectSync,
} from '../../../../redux/actions/networkActions';
import urls from '../../../../routing/urls';
import SyncSourceModal from '../SyncSourceModal/SyncSourceModal';

import LinkedInImport from './LinkedInImport/LinkedInImport';

import styles from './SyncSources.module.scss';

const SyncSources = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showSyncModal, setShowSyncModal] = useState(false);
  const syncSources = useSelector((state) => state.networkReducer.syncSources);
  const importHistoryCount = useSelector((state) => state.networkReducer.importHistoryCount);

  const handleToggleModal = useCallback(() => {
    setShowSyncModal((prevState) => !prevState);
  }, []);

  useEffect(() => {
    dispatch(getSyncData());
    dispatch(fetchImportHistoryCount());
  }, []);

  const handleToggleConnectSync = useCallback(
    (e, id) => {
      dispatch(toggleConnectSync(id, e));
    },
    [dispatch]
  );

  const handleDeleteSync = useCallback(
    (id) => {
      dispatch(deleteSync(id));
    },
    [dispatch]
  );

  return (
    <div className={styles.rootCont}>
      <SyncSourceModal showModal={showSyncModal} handleToggleModal={handleToggleModal} />
      <div className={classNames(styles.backCont, styles.web)}>
        <BackButton onBack={() => navigate(urls.network)} />
      </div>
      <div className={classNames(styles.sectionCont, styles.sectionContMobile)}>
        <div className={classNames(styles.headingContainer)}>
          <p className={classNames(styles.heading, styles.headingTop)}>{t('syncSources.addYourContacts')}</p>
          <div
            className={classNames(styles.closeBtn, styles.mobile)}
            role="presentation"
            onClick={() => navigate(urls.network)}
          >
            <CrossIcon />
          </div>
        </div>
        <p className={styles.info}>{t('syncSources.syncDescription')}</p>
      </div>
      <div className={classNames(styles.sectionCont, styles.sectionContMob)}>
        <p className={styles.heading}>{t('syncSources.syncSources')}</p>
        <div className={styles.syncCont}>
          {!!syncSources.length && (
            <div className={classNames(styles.syncRowCont, styles.headerRow)}>
              <p className={classNames(styles.rowItem, styles.sourceColumn)}>{t('syncSources.sources')}</p>
              <p className={classNames(styles.rowItem2, styles.syncColumn)}>{t('syncSources.sync')}</p>
              <p className={classNames(styles.rowItem3, styles.web)}>{t('syncSources.lastSync')}</p>
              <div className={styles.rowItem2} />
            </div>
          )}
          {syncSources.map((sourceObj) => (
            <div className={styles.syncRowCont} key={sourceObj.id}>
              <div className={classNames(styles.rowItem, styles.sourceColumn)}>
                <div className={styles.sourceCont}>
                  <div className={styles.iconCont}>
                    {sourceObj.source === 'google' && <GoogleIcon />}
                    {sourceObj.source === 'outlook' && <MicrosoftIcon />}
                  </div>
                  <p className={styles.sourceName}>{sourceObj.email}</p>
                </div>
              </div>
              <div className={classNames(styles.rowItem2, styles.syncColumn)}>
                <Switch checked={sourceObj.is_synced} onChange={(e) => handleToggleConnectSync(e, sourceObj.id)} />
              </div>
              <p className={classNames(styles.rowItem3, styles.syncDate, styles.web)}>
                {moment(sourceObj.last_synced).format('hh:mm A MM/DD/YYYY')}
              </p>
              <div className={classNames(styles.rowItem2, styles.web)}>
                <CrossIcon className={styles.close} onClick={() => handleDeleteSync(sourceObj.id)} />
              </div>
              <div className={classNames(styles.rowItem2, styles.mobile)}>
                <Menu
                  items={[
                    {
                      title: t('delete'),
                      action: () => handleDeleteSync(sourceObj.id),
                    },
                  ]}
                />
              </div>
            </div>
          ))}
          <Button className={styles.addBtn} onClick={handleToggleModal}>
            <AddIcon />
            {t('syncSources.addSyncSource')}
          </Button>
        </div>
      </div>
      <div className={classNames(styles.sectionCont, styles.sectionContMob)}>
        <p className={styles.heading}>{t('syncSources.importFromFiles')}</p>
        <LinkedInImport showCounter={!!importHistoryCount?.linkedin} />
        <div className={styles.csvImportCont}>
          <Button className={styles.addBtn} onClick={() => navigate(urls.networkCsvImport)}>
            <AddIcon />
            {t('syncSources.importCsvVcard')}
          </Button>
          {!!importHistoryCount?.other && (
            <button type="button" className={styles.viewPastLink} onClick={() => navigate(urls.csvImportHistory)}>
              {t('syncSources.viewPastImports')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SyncSources;
